import React, {useEffect, useRef, useMemo} from 'react'
import {observer} from 'mobx-react-lite'
import {TObservablePage} from '../../types/commerce'
import {Layout} from '../../components/layout'
import {Container, Stack} from '../../vanilla'
import {flowResult} from 'mobx'
import {useBasketStore, useContentStore, useCustomerStore, useOrderStore} from '../../store/hooks/useStore'
import {Slot, useContentToDisplay} from '../../components/cms/Slot'
import {SlotContentItem, SlotItem} from '../../types/cms'
import {Seo} from '../../components/seo'
import {BookDelivery} from '../../components/book-delivery'
import {EditOrderStickyBanner} from '../../components/edit-order-sticky-banner'
import {usePageDataTracking} from '../../analytics/page-tracking'
import {usePromotionDataTracking, useRegisterPromotionsForTracking} from '../../analytics/promotion-tracking'
import {DisplaySlots} from '../category/partials/display-slots'
import {useIntersectionObserver} from '../../hooks/use-intersection-observer'
import { HomePageType } from '../../types/cms/navigation'
import { CONTENT_SCHEMAS } from '../../components/cms/utils'
import { buildHomepageStructuredData, buildWebsiteStructuredData } from '../../utils/structured-data'

interface HomePageProps {
    content?: HomePageType
    children?: React.ReactNode
  }

const Home: TObservablePage<HomePageProps> = observer(({content: visualizationContent}) => {
  const contentStore = useContentStore()
  const {editMode, recentOrder} = useOrderStore()
  const {currentDeliverySlotId, postcodeError} = useBasketStore()
  const {isRegistered} = useCustomerStore()
  const content = visualizationContent ?? contentStore.contentById['homepage']
 
  const sendPageData = usePageDataTracking()
  const pageType = 'storefront'
  let pageCategory = ''

  if (isRegistered) {
    pageCategory = 'Full-login'
  } else {
    pageCategory = 'Default'
  }
  const sendPromotionData = usePromotionDataTracking()
  const registerPromotions = useRegisterPromotionsForTracking()
  const heroRef = useRef<HTMLDivElement | null>(null)
  const {isOnScreen: isHeroInView} = useIntersectionObserver(heroRef, {
    useOnce: true,
    rootMargin: '-70px 0px -70px 0px',
  })

  const heroSlots = content?.slots
    // Add index position of slots array for tracking purposes
    .map((slotGroup: SlotContentItem, index: number) => ({ slotGroup, originalIndex: index }))
    .filter(({ slotGroup }: { slotGroup: SlotContentItem; originalIndex: number }) => slotGroup._meta.schema === CONTENT_SCHEMAS.HOME_HERO)
    .map(({ slotGroup, originalIndex }: { slotGroup: SlotContentItem; originalIndex: number }) => {
      const transformedSlots = useContentToDisplay(slotGroup)
      return transformedSlots?.map((slot: SlotItem) => ({
        ...slot,
        slotGroupName: slotGroup._meta.name,
        slotIndex: originalIndex + 1
      })) || []
    }) || []

  let otherSlots = content?.slots
    // Add index position of slots array for tracking purposes
    .map((slotGroup: SlotContentItem, index: number) => ({ slotGroup, originalIndex: index }))
    .filter(({ slotGroup }: { slotGroup: SlotContentItem; originalIndex: number }) => slotGroup._meta.schema !== CONTENT_SCHEMAS.HOME_HERO)
    .map(({ slotGroup, originalIndex }: { slotGroup: SlotContentItem; originalIndex: number }) => {
      const transformedSlots = useContentToDisplay(slotGroup)
      return transformedSlots?.map((slot: SlotItem) => ({
        ...slot,
        slotGroupName: slotGroup._meta.name,
        })) || []
    }).filter((slotGroup: SlotContentItem) => slotGroup.length > 0) || []

  otherSlots = [...otherSlots].map((slotGroup, groupIndex) =>
    slotGroup.map((slot: SlotContentItem) => ({
    ...slot,
    slotIndex: heroSlots.length + groupIndex + 1
  })))

  useEffect(() => {
    sendPageData(pageType, pageCategory)
  }, [isRegistered])

  useEffect(() => {
    registerPromotions(otherSlots)
  }, [])

  useEffect(() => {
    if (isHeroInView && heroSlots.length > 0) {
      sendPromotionData(heroSlots)
    }
  }, [isHeroInView])

  const structuredData = useMemo(() => {
    return [buildHomepageStructuredData(), buildWebsiteStructuredData()]
  }, [])

  if (!content) {
    return null
  }

  return (
    <Layout>
      {editMode && <EditOrderStickyBanner />}
      <Seo
        title={content?.SEO?.pageTitle || 'Home Page | Iceland'}
        description={content?.SEO?.pageDescription}
        structuredData={structuredData}
      />
      <Stack height={['260px', '260px', '320px']} ref={heroRef}>
        {heroSlots?.map((slotGroup: SlotContentItem, idx: number) => (
          <Slot key={`${slotGroup?._meta?.deliveryId}-${idx}`} data={slotGroup} />
        ))}
      </Stack>
      <Stack overflow={'auto'} height={currentDeliverySlotId ? ['292px', '122px'] : recentOrder ? ['196px', '96px'] : postcodeError ? ['114px', '122px'] : ['auto', '96px'] }>
        <BookDelivery />
      </Stack>
      <Container paddingBottom={['32px', '48px']}>
        <Stack spacing={['16px', '24px']}>
          {content?.slots
            .filter((slotGroup: SlotContentItem) => slotGroup._meta.schema !== CONTENT_SCHEMAS.HOME_HERO)
            .map((slotGroup: SlotContentItem, idx: number) => (
              <DisplaySlots
                key={`${slotGroup?._meta?.deliveryId}-${idx}`}
                slots={slotGroup}
                slotIndex={heroSlots.length + idx + 1}
                slotGroupName={slotGroup._meta?.name}
              />
            ))
          }
        </Stack>
      </Container>
    </Layout>
  )
})

Home.getProps = async ({store}) => {
  const {contentStore} = store
  const {fetchItemByKey} = contentStore
  await flowResult(fetchItemByKey('homepage'))
}

export default Home
